import React, { Suspense, lazy, useEffect, useRef, useState, useCallback } from 'react'
import classNames from 'classnames'
import { fetchMarketById, selectMarketById, selectMarketErrorById, selectMarketStatusById } from '@/store/markets'
import { i18n } from '@/locale/i18n'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { notFoundUrl } from '@/urls'
import { useActiveCurrency, usePageGround } from '@/hooks'
import useI18n from '@/hooks/useI18n'
import { useDispatch, useSelector } from 'react-redux'
import constants from '@/constants'
import { WAGER_STATUS } from '@/constants/marketBackendEnums'
import Market from '@/features/market/market'
import './ViewMarketPage.scss'
import { selectActiveLanguage, selectActiveMarketType, selectPageLayout, setOutcomeSelected } from '@/store/app'
import store from '@/store'
import { getWagerSelected, selectWagerSelected } from '@/store/wagers'
import { useAutoAnimate } from '@formkit/auto-animate/react'
// import html2canvas from 'html2canvas'
import HelmetMarketImage from '@/features/market/helmet-market-image'
import MarketPageHeader from '@/features/market/market-page-header'
import MarketStickyBar from '@/features/market/market-sticky-bar'
import { fetchQuestionBetHistory, resetBetHistory } from '@/store/wagers'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import ErrorHandler from '@/components/ui/error-handler'
import ErrorService from '@/services/errors/ErrorService'
import { registerMarketPriceChangeCallback } from '@/hooks/useRealTimeMarket'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import { deriveMarketUi } from '@/features/market/utils/marketType'
import PlaceBet from '@/features/market/place-bet'
import { isSportsEvent, isSportsMatchLive } from '@/features/market/utils/sportsEvent'

const SportEventLiveStats = lazy(() => import('@/features/market/sport-event/SportEventLiveStats'))

// Add shimmer animation styles
const shimmerStyles = `
  @keyframes shimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`

// Inject styles if not already present (client only; skip during SSR)
if (!import.meta.env.SSR && typeof document !== 'undefined' && !document.getElementById('market-page-shimmer-styles')) {
  const style = document.createElement('style')
  style.id = 'market-page-shimmer-styles'
  style.textContent = shimmerStyles
  document.head.appendChild(style)
}

// Shimmer effect component
const ShimmerEffect = ({ className = '', children }) => {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      {children}
      <div
        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
        style={{
          animation: 'shimmer 2s infinite',
          transform: 'translateX(-100%)',
        }}></div>
    </div>
  )
}

// Market Card Skeleton for ViewMarketPage
const MarketCardSkeleton = () => (
  <ShimmerEffect className="overflow-hidden mb-4">
    <article className="animate-pulse">
      <div className="p-4 space-y-4">
        <div className="space-y-3">
          {/* Categories */}
          <div className="flex gap-2">
            <div className="h-6 bg-surface-3 rounded-full w-16"></div>
            <div className="h-6 bg-surface-3 rounded-full w-20"></div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <div className="h-6 bg-surface-3 rounded w-full"></div>
            <div className="h-6 bg-surface-3 rounded w-3/4"></div>
          </div>

          {/* Metadata */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-surface-3 rounded"></div>
              <div className="h-4 bg-surface-3 rounded w-24"></div>
            </div>
            <div className="h-4 bg-surface-3 rounded w-16"></div>
          </div>
        </div>

        {/* Outcomes */}
        <div className="flex flex-col gap-2">
          {[1, 2].map(i => (
            <div
              key={i}
              className="flex justify-between items-center p-3 bg-surface-2 border border-Neutral-3 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-surface-3 rounded-full"></div>
                <div className="space-y-1">
                  <div className="h-4 bg-surface-3 rounded w-20"></div>
                  <div className="h-3 bg-surface-3 rounded w-16"></div>
                </div>
              </div>
              <div className="text-right space-y-1">
                <div className="h-5 bg-surface-3 rounded w-12"></div>
                <div className="h-3 bg-surface-3 rounded w-8"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </article>
  </ShimmerEffect>
)

const ChartSkeleton = () => (
  <ShimmerEffect className="mb-4">
    <div className="animate-pulse space-y-4">
      {/* Chart header */}
      <div className="flex justify-between items-center">
        <div className="h-6 bg-surface-3 rounded w-32"></div>
        <div className="flex gap-2">
          <div className="h-8 bg-surface-3 rounded w-16"></div>
          <div className="h-8 bg-surface-3 rounded w-16"></div>
        </div>
      </div>

      {/* Chart area */}
      <div className="h-64 bg-surface-3 rounded-lg"></div>

      {/* Chart footer */}
      <div className="flex justify-between items-center">
        <div className="h-4 bg-surface-3 rounded w-24"></div>
        <div className="h-4 bg-surface-3 rounded w-20"></div>
      </div>
    </div>
  </ShimmerEffect>
)

const SummarySkeleton = () => (
  <ShimmerEffect className="mb-4">
    <div className="animate-pulse space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 bg-surface-3 rounded-full"></div>
        <div className="h-6 bg-surface-3 rounded w-32"></div>
      </div>

      {/* Content lines */}
      <div className="space-y-3">
        <div className="h-4 bg-surface-3 rounded w-full"></div>
        <div className="h-4 bg-surface-3 rounded w-5/6"></div>
        <div className="h-4 bg-surface-3 rounded w-4/6"></div>
        <div className="h-4 bg-surface-3 rounded w-3/4"></div>
      </div>

      {/* Tags or metadata */}
      <div className="flex gap-2 mt-4">
        <div className="h-6 bg-surface-3 rounded-full w-16"></div>
        <div className="h-6 bg-surface-3 rounded-full w-20"></div>
      </div>
    </div>
  </ShimmerEffect>
)

const ActivityTabsSkeleton = () => (
  <ShimmerEffect className="mb-4">
    <div className="animate-pulse space-y-6">
      {/* Tab list: bet history / comments */}
      <div className="flex gap-2">
        <div className="h-8 bg-surface-3 rounded-lg w-32"></div>
        <div className="h-8 bg-surface-3 rounded-lg w-28"></div>
      </div>

      {/* Search + filters */}
      <div className="flex justify-between items-center">
        <div className="h-8 bg-surface-3 rounded w-full max-w-[240px]"></div>
        <div className="flex gap-2">
          <div className="h-8 bg-surface-3 rounded w-20"></div>
          <div className="h-8 bg-surface-3 rounded w-16"></div>
        </div>
      </div>

      {/* Bet entries */}
      <div className="space-y-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="flex justify-between items-center p-4 border border-Neutral-3 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-surface-3 rounded-full"></div>
              <div className="space-y-1">
                <div className="h-4 bg-surface-3 rounded w-24"></div>
                <div className="h-3 bg-surface-3 rounded w-20"></div>
              </div>
            </div>
            <div className="text-right space-y-1">
              <div className="h-5 bg-surface-3 rounded w-16"></div>
              <div className="h-3 bg-surface-3 rounded w-12"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </ShimmerEffect>
)

const MarketPageSkeleton = () => (
  <div className="space-y-0">
    <MarketCardSkeleton />
    <SummarySkeleton />
    <ChartSkeleton />
    <ActivityTabsSkeleton />
  </div>
)

// PlaceBet Skeleton for sidebar
const PlaceBetPageSkeleton = () => (
  <ShimmerEffect className="flex w-[340px] flex-col rounded-lg border border-Neutral-3 bg-surface-2">
    <div className="space-y-3 !p-5">
      <div className="flex w-full gap-2 mb-6">
        <div className="flex justify-center items-center h-7 px-4 w-16 rounded-full bg-surface-3"></div>
        <div className="flex justify-center items-center h-7 px-4 w-16 rounded-full bg-surface-3"></div>
      </div>
      {/* Outcome selector skeleton */}
      <div className="flex w-full gap-2">
        <div className="flex justify-center items-center h-12 px-4 w-full rounded-lg bg-surface-3"></div>
        <div className="flex justify-center items-center h-12 px-4 w-full rounded-lg bg-surface-3"></div>
      </div>

      {/* Amount input skeleton */}
      <div className="space-y-3">
        <div className="relative h-14 flex gap-2 items-center bg-surface-2 rounded-lg !p-3 w-full border border-Neutral-3">
          <div className="flex-1 h-4 bg-surface-3 rounded w-24"></div>
          <div className="h-3 bg-surface-3 rounded w-12"></div>
        </div>
        <div className="flex justify-between">
          <div className="h-3 bg-surface-3 rounded w-20"></div>
        </div>
      </div>
    </div>

    {/* Bottom section skeleton */}
    <div className="flex justify-between !px-5 !py-3">
      <div className="flex flex-col justify-center items-start">
        <div className="h-4 bg-surface-3 rounded w-16 mb-1"></div>
        <div className="h-5 bg-surface-3 rounded w-20"></div>
      </div>

      <div className="h-12 bg-surface-3 rounded-lg w-24"></div>
    </div>
  </ShimmerEffect>
)

// PlaceBet Error Fallback Component
const PlaceBetErrorFallback = ({ onRetry, error, question, outcome, position, onToggle, open, onCancel }) => {
  // Track the error when component renders
  React.useEffect(() => {
    if (error) {
      ErrorService.captureError(error, {
        component: 'PlaceBet-MarketPage',
        type: 'react_error',
        currentUrl: location.pathname,
        context: {
          questionId: question?.id,
          outcomeId: outcome?.id,
          position: position,
        },
      })
    }
  }, [error])

  const { longLabel, shortLabel } = getPositionLabels(outcome, question)

  return (
    <div
      className="flex flex-col w-full rounded-lg border-2 border-negative-4 bg-surface-2"
      data-testid="place-bet-error-boundary"
      data-source="market-page">
      <div className="space-y-3 !p-5">
        {/* Error header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-negative-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <span className="text-negative-4 font-medium">{i18n('common.betting_panel_error')}</span>
          </div>
        </div>

        {/* Outcome selector - matching BuyPlaceBet structure */}
        <div className="flex w-full gap-2 opacity-60">
          <button
            className={classNames(
              'flex justify-center items-center h-12 px-4 w-full rounded-lg transition-all duration-200',
              'bg-positive-5/30 text-Neutral-6',
              {
                'font-bold !bg-positive-3 !text-white': position === constants.position.LONG,
                '!bg-Neutral-3 !text-Neutral-4': position !== constants.position.LONG,
              }
            )}>
            <span className="text-lg font-medium">{longLabel}</span>
          </button>
          <button
            className={classNames(
              'flex justify-center items-center h-12 px-4 w-full rounded-lg transition-all duration-200',
              'bg-auxiliaries-3/30 text-Neutral-6',
              {
                'font-bold !bg-auxiliaries-3 !text-white': position === constants.position.SHORT,
                '!bg-Neutral-3 !text-Neutral-4': position !== constants.position.SHORT,
              }
            )}>
            <span className="text-lg font-medium">{shortLabel}</span>
          </button>
        </div>

        {/* Amount input - matching BuyPlaceBet PlaceBetInput structure */}
        <div className="opacity-60">
          <div className="relative h-14 flex gap-2 items-center bg-surface-2 transition duration-200 rounded-lg !p-3 w-full border border-solid border-Neutral-3">
            <div className="flex-1 text-Neutral-4">Enter amount...</div>
            <div className="text-xs text-Neutral-4">OOM</div>
          </div>
          <div className="flex justify-between mt-1">
            <div className="text-xs text-Neutral-4">Price per share ..</div>
          </div>
        </div>
      </div>

      {/* Bottom section - matching BuyPlaceBet structure */}
      <div className="flex justify-between !px-5 !py-3">
        {/* To win section */}
        <div className="flex flex-col justify-center items-start opacity-60">
          <span className="text-sm text-Neutral-4">{i18n('place_bet.to_win')}</span>
          <span className="font-bold text-Neutral-6 leading-snug">-- OOM</span>
        </div>

        {/* Retry button styled like bet button */}
        <button
          onClick={onRetry}
          className="h-12 px-6 font-bold text-white rounded-lg bg-negative-4 hover:bg-negative-5 transition-colors">
          {i18n('common.retry')}
        </button>
      </div>

      {/* Error message */}
      <div className="px-5 pb-3">
        <div className="text-negative-4 text-xs text-center" data-testid="place-bet-error-message">
          {error?.message || i18n('common.failed_to_load_betting_panel')}
        </div>
      </div>
    </div>
  )
}

const ViewMarketPage = props => {
  const { questionId } = useParams()
  const { className, style } = props
  const dispatch = useDispatch()
  useI18n()
  const marketType = useSelector(selectActiveMarketType)
  const pageLayout = useSelector(selectPageLayout)
  const navigate = useNavigate()
  const viewRef = useRef()
  const locale = useSelector(selectActiveLanguage())
  const activeCurrency = useActiveCurrency()
  usePageGround('surface-2')
  const questionFromStore = useSelector(selectMarketById(questionId))
  // Use preloaded state when present (SSR), including key mismatch (string vs number id)
  const preloadedPool =
    typeof window !== 'undefined' && window.__PRELOADED_STATE__?.markets?.pool?.data
  const questionFromPreload = preloadedPool?.[questionId] ?? preloadedPool?.[Number(questionId)]
  const question = questionFromStore ?? questionFromPreload

  const location = useLocation()
  const [position, setPosition] = useState(location.state?.position || constants.position.LONG)

  const questionError = useSelector(selectMarketErrorById(questionId))
  const questionStatus = useSelector(selectMarketStatusById(questionId))
  const isLoading = questionStatus === constants.LOADING || (question && !question.complete)
  const isMobileSize = useIsMobileSize()

  // The condensed bar takes over once the full header has scrolled behind the
  // navbar, so the header's own bottom edge is measured against a navbar inset.
  // Measured on scroll and on resize rather than with an IntersectionObserver:
  // the observer only reports threshold crossings, so a header that is still
  // growing (images, lazy blocks) on its first callback keeps whatever answer
  // that early measurement gave until it happens to cross the threshold again.
  const headerRef = useRef(null)
  const [headerScrolledPast, setHeaderScrolledPast] = useState(false)

  useEffect(() => {
    const node = headerRef.current
    if (!node) {
      setHeaderScrolledPast(false)
      return
    }

    let frame = null

    const measure = () => {
      frame = null
      const navbarHeight =
        parseInt(getComputedStyle(document.documentElement).getPropertyValue('--navbar-desktop-height'), 10) || 96
      setHeaderScrolledPast(node.getBoundingClientRect().bottom <= navbarHeight)
    }

    const schedule = () => {
      if (frame != null) return
      frame = requestAnimationFrame(measure)
    }

    measure()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)

    // The header keeps growing after mount (crests, chart, lazy blocks), and a
    // taller header moves the edge this depends on.
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null
    resizeObserver?.observe(node)

    return () => {
      if (frame != null) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      resizeObserver?.disconnect()
    }
  }, [isMobileSize, question?.id])

  /**
   * Hydration-aware ref: when SSR populates `markets.pool` for this question
   * the marketType-change effect would otherwise trigger an immediate refetch
   * just because `marketType` arrives async on the client. Skip exactly one
   * refetch per questionId when the SSR data is fresh.
   */
  const ssrHydratedQuestionRef = useRef(null)
  const lastScrolledQuestionIdRef = useRef(null)

  // First outcome (Yes) for yes/no questions on desktop once the market is loaded; null otherwise
  const getDefaultOutcome = () => {
    if (isMobileSize || !question || questionStatus !== constants.COMPLETE) return null
    if (!deriveMarketUi(question).binaryLabelStyle || !(question.outcomes?.length >= 2)) return null
    return question.outcomes[0]
  }
  const getInitialOutcome = () => location.state?.outcome || getDefaultOutcome()
  const [selectedOutcome, setSelectedOutcome] = useState(getInitialOutcome)
  const [placebetOpen, setPlacebetOpen] = useState(false)
  const [animationParent] = useAutoAnimate()
  const marketRef = useRef(null)
  const setActiveTabRef = useRef(null)

  // Register callback for real-time market price updates
  const handleMarketPriceChange = useCallback(outcomes => {
    if (marketRef.current?.refreshChart) {
      marketRef.current.refreshChart(outcomes)
    }
  }, [])

  useEffect(() => {
    if (!questionId) return

    const unregister = registerMarketPriceChangeCallback(questionId, handleMarketPriceChange)

    return unregister
  }, [questionId, handleMarketPriceChange])

  useEffect(() => {
    if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
      performance.mark('view-market-hydrated')
    }
  }, [])

  // Clear the selected wager/outcome when leaving the market page so a stale selection
  // (e.g. from clicking Sell) can't leak into the next route and drive render loops.
  useEffect(() => {
    return () => {
      dispatch(getWagerSelected(null))
      dispatch(setOutcomeSelected(null))
    }
  }, [dispatch])

  useEffect(() => {
    if (typeof window === 'undefined') return

    let cancelled = false
    let preloaded = false
    const preload = () => {
      if (cancelled || preloaded) return
      preloaded = true
      Promise.all([
        import('@/features/market/place-bet'),
        import('@/features/market/order-book'),
        import('@/features/bet-history'),
        import('@/features/comments/CommentPanel'),
        import('@/features/market/related-markets'),
      ]).catch(() => {})
    }

    const interactionHandler = () => preload()
    window.addEventListener('pointerdown', interactionHandler, { once: true, passive: true })
    window.addEventListener('touchstart', interactionHandler, { once: true, passive: true })

    let idleHandle = null
    const isIdleCallbackSupported = 'requestIdleCallback' in window && typeof window.requestIdleCallback === 'function'

    if (isIdleCallbackSupported) {
      idleHandle = window.requestIdleCallback(preload, { timeout: 2000 })
    } else {
      idleHandle = window.setTimeout(preload, 2000)
    }

    return () => {
      cancelled = true
      window.removeEventListener('pointerdown', interactionHandler)
      window.removeEventListener('touchstart', interactionHandler)
      if (isIdleCallbackSupported && idleHandle !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle)
      }
      if (!isIdleCallbackSupported && idleHandle !== null) {
        window.clearTimeout(idleHandle)
      }
    }
  }, [])

  // Fetch market as soon as we have questionId (during full-screen loading too, so data is ready when loader ends)
  const lastFetchedMarketKeyRef = useRef(null)
  useEffect(() => {
    if (!questionId) return
    const fetchKey = `${questionId}|${marketType}|${activeCurrency.id}`
    if (lastFetchedMarketKeyRef.current === fetchKey && questionStatus === constants.COMPLETE && question?.complete) return
    if (questionStatus === constants.LOADING) return
    lastFetchedMarketKeyRef.current = fetchKey
    dispatch(fetchMarketById(questionId, marketType))
  }, [questionId, marketType, activeCurrency.id])
  useEffect(() => {
    if (questionStatus === constants.ERROR && questionError.status === 404) {
      navigate(notFoundUrl(), { replace: true })
    }
  }, [questionStatus])
  /* Deep links that point at something further down the page own the scroll
     position — resetting to the top would undo the anchoring. */
  const isDeepLinked = typeof window !== 'undefined' && window.location.search.includes('shared_comment_id=')

  useEffect(() => {
    if (questionStatus !== constants.COMPLETE) return
    if (lastScrolledQuestionIdRef.current === questionId) return
    lastScrolledQuestionIdRef.current = questionId
    if (isDeepLinked) return
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }, 200)
  }, [questionId, questionStatus])

  // The route keeps this instance across /q/:questionId changes, so the selection the page
  // starts with is re-derived for each market instead of carrying over from the last one.
  const selectionQuestionIdRef = useRef(questionId)
  useEffect(() => {
    if (selectionQuestionIdRef.current === questionId) return
    selectionQuestionIdRef.current = questionId
    setPosition(location.state?.position || constants.position.LONG)
    setSelectedOutcome(getInitialOutcome())
  }, [questionId])

  // Auto-select the default outcome once the market loads, if no outcome is selected
  useEffect(() => {
    if (selectedOutcome) return
    const defaultOutcome = getDefaultOutcome()
    if (defaultOutcome) setSelectedOutcome(defaultOutcome)
  }, [question, questionStatus, isMobileSize])

  // const renderBreadcrumb = () => {
  //   const doc = {
  //     '@context': 'https://schema.org',
  //     '@type': 'BreadcrumbList',
  //     itemListElement: [],
  //   }

  //   question.category.forEach((category, index) => {
  //     const baseUrl = import.meta.env.VITE_SHARE_PATH || import.meta.env.VITE_FRONT_URL.slice(0, -1) || ''
  //     const url = new URL(categoryListUrl(category), baseUrl).href
  //     doc.itemListElement.push({
  //       '@type': 'ListItem',
  //       position: index + 1,
  //       name: category.title,
  //       item: url,
  //     })
  //   })

  //   return <script type="application/ld+json">{JSON.stringify(doc)}</script>
  // }

  const renderLoading = () => {
    return (
      <div className={classNames('question-details', className)} style={style}>
        <div className="question-details__question-card  mt-[54px]">
          <MarketPageSkeleton />
        </div>
        {/* Desktop: Sidebar skeleton */}
        <aside className="hidden md:flex! md:flex-col md:w-[340px] md:sticky md:top-[126px] md:pt-[54px]">
          <PlaceBetPageSkeleton />
        </aside>
      </div>
    )
  }

  const renderMakeBet = () => {
    const updatedOutcome =
      selectedOutcome && question.outcomes && question.outcomes.find(o => o.id === selectedOutcome.id)

    return (
      <div className="w-full">
        {question && (
          <ErrorHandler
            component="MarketPage.PlaceBet"
            fallback={({ error, resetErrorBoundary }) => (
              <PlaceBetErrorFallback
                onRetry={resetErrorBoundary}
                error={error}
                question={question}
                outcome={updatedOutcome}
                position={position}
                onToggle={next => setPlacebetOpen(v => (typeof next === 'boolean' ? next : !v))}
                onCancel={() => setSelectedOutcome(null)}
                open={placebetOpen}
              />
            )}>
            <PlaceBet
              question={question}
              open={placebetOpen}
              outcome={updatedOutcome}
              onChangeOutcome={setSelectedOutcome}
              onCancel={() => {
                setSelectedOutcome(null)
                dispatch(setOutcomeSelected(null))
              }}
              setPlacebetOpen={setPlacebetOpen}
              onToggle={next => setPlacebetOpen(v => (typeof next === 'boolean' ? next : !v))}
              position={position}
              setPosition={setPosition}
              onWagerAction={() => {
                dispatch(resetBetHistory({ questionId: question.id }))
                dispatch(fetchQuestionBetHistory(question, {}, true))
              }}
              place="Market page"
              inMarketPage={true}
              onOpenOrderbook={() => marketRef.current?.openOrderbook?.()}
              onScrollToOrderbook={() => marketRef.current?.scrollToOrderbook?.()}
              onSelectOrderbookPrice={price => marketRef.current?.selectOrderbookPrice?.(price)}
              onSetActiveTabRef={setActiveTabRef}
            />
          </ErrorHandler>
        )}
      </div>
    )
  }

  const render = () => {
    // Show skeleton only when we don't have market data (same pattern as feed: show content when we have it)
    if (!question) {
      return renderLoading()
    }

    return (
      <Suspense fallback={renderLoading()}>
        <div
          className={classNames('question-details', className, {
            // Sports fixtures keep the header in the left column so the bet pad
            // starts level with it instead of below a full-width header.
            'question-details--fixture': !isMobileSize && isSportsEvent(question),
          })}
          style={style}
          ref={viewRef}>
          {!isMobileSize && <MarketStickyBar question={question} visible={headerScrolledPast} />}
          {/* display: contents by default, so this wrapper only becomes a column
              in the fixture layout — see ViewMarketPage.scss. */}
          <div className="question-details__main">
            {!isMobileSize && (
              <div className="question-details__header" ref={headerRef}>
                <MarketPageHeader question={question} categories={question.category} showSummary />
              </div>
            )}
            <div className="question-details__question-card" ref={animationParent}>
              <Market
                ref={marketRef}
                className="question-card__maindetails"
                bet={question.wager}
                categories={question.category}
                inlineBehavior
                inMarketPage={true}
                question={question}
                showChart
                showLatestBets
                showMakeBet
                showSelectedOutcome
                showSharePanel
                showSummary
                showComments
                showRelatedMarket
                showOrderBook={question.order_book_enabled}
                showBetStatus
                loadingType="page"
                currentOutcomeSelection={selectedOutcome}
                position={position}
                setPosition={setPosition}
                onSetActiveTab={tab => {
                  if (setActiveTabRef.current) {
                    setActiveTabRef.current(tab)
                  }
                  // Open PlaceBet panel when tab is changed
                  setPlacebetOpen(true)
                }}
                onOutcomeSelected={(outcome, pos, clickedWager) => {
                  // Clear any previously selected outcome first (like MarketCard does)
                  dispatch(setOutcomeSelected(null))
                  setSelectedOutcome(outcome)
                  dispatch(setOutcomeSelected(outcome))

                  // On mobile PlaceBet lives in a Sheet driven by `placebetOpen`; selecting an
                  // outcome has to open it. Runs before the early returns below.
                  setPlacebetOpen(true)

                  // Handle position: support both old format ('yes'/'no') and new format (constants.position.LONG/SHORT)
                  let normalizedPos = null
                  if (pos === 'yes' || pos === 'no') {
                    normalizedPos = pos === 'yes' ? constants.position.LONG : constants.position.SHORT
                    setPosition(normalizedPos)
                  } else if (pos === constants.position.LONG || pos === constants.position.SHORT) {
                    normalizedPos = pos
                    setPosition(normalizedPos)
                  }

                  // When a specific wager was clicked (e.g. the Sell button in the bet list),
                  // keep that exact wager. Two wagers can share the same outcome and position but
                  // differ by currency, so re-deriving from outcome/position alone would pick the
                  // wrong one (always the first match) and ignore the clicked currency.
                  if (clickedWager && clickedWager.id != null) {
                    const exactWager = question?.wagers?.find(w => w.id === clickedWager.id) || clickedWager
                    dispatch(getWagerSelected({ ...exactWager, _diff: Math.random() }))
                    return
                  }

                  // Sync wager when outcome is selected
                  if (outcome && question?.wagers) {
                    // Keep the already selected wager only when it matches the clicked outcome and,
                    // when a position was provided, the clicked position. This prevents the long (YES)
                    // and short (NO) positions of the same outcome from overriding each other.
                    const currentWager = selectWagerSelected()(store.getState())
                    if (
                      currentWager?.outcome?.id === outcome.id &&
                      (!normalizedPos || currentWager.position === normalizedPos)
                    ) {
                      return
                    }

                    // Prefer the wager matching the clicked position, then the previously selected
                    // currency/position, then the first available position for this outcome.
                    const candidates = question.wagers.filter(w => w.status === WAGER_STATUS.PURCHASE && w.outcome?.id === outcome.id)
                    const matchingWager =
                      (normalizedPos && candidates.find(w => w.position === normalizedPos)) ||
                      (currentWager && candidates.length > 1
                        ? candidates.find(
                            w => w.currency === currentWager.currency && w.position === currentWager.position
                          ) || candidates[0]
                        : candidates[0])

                    if (matchingWager) {
                      dispatch(getWagerSelected(matchingWager))
                    } else {
                      const firstWager = question.wagers.find(w => w.status === WAGER_STATUS.PURCHASE)
                      if (firstWager) {
                        dispatch(getWagerSelected(firstWager))
                      }
                    }
                  }
                }}
                onPriceClick={(price, outcome, clickedPosition) => {
                  // The order book row belongs to one side of the market, so the clicked position
                  // has to travel with the outcome — mono-custom markets identify the selected
                  // "market" in PlaceBet as outcome + position.
                  if (clickedPosition === constants.position.LONG || clickedPosition === constants.position.SHORT) {
                    setPosition(clickedPosition)
                  }

                  // Select the outcome associated with the orderbook when a price is clicked
                  if (outcome) {
                    // Clear any previously selected outcome first (like onOutcomeSelected does)
                    dispatch(setOutcomeSelected(null))
                    setSelectedOutcome(outcome)
                    dispatch(setOutcomeSelected(outcome))

                    // Sync wager when outcome is selected
                    if (question?.wagers) {
                      const currentWager = selectWagerSelected()(store.getState())
                      if (currentWager?.outcome?.id === outcome.id) {
                        setPlacebetOpen(true)
                        return
                      }

                      const candidates = question.wagers.filter(w => w.status === WAGER_STATUS.PURCHASE && w.outcome?.id === outcome.id)
                      const matchingWager =
                        currentWager && candidates.length > 1
                          ? candidates.find(
                              w => w.currency === currentWager.currency && w.position === currentWager.position
                            ) || candidates[0]
                          : candidates[0]

                      if (matchingWager) {
                        dispatch(getWagerSelected(matchingWager))
                      } else {
                        const firstWager = question.wagers.find(w => w.status === WAGER_STATUS.PURCHASE)
                        if (firstWager) {
                          dispatch(getWagerSelected(firstWager))
                        }
                      }
                    }
                  }
                  // Open PlaceBet on mobile when a price is clicked
                  setPlacebetOpen(true)
                }}
              />
            </div>
          </div>

          {/* Desktop: Sidebar with PlaceBet */}
          {!isMobileSize && (
            <aside className="question-details__bet-sidebar flex md:flex-col md:sticky md:pb-[54px]">
              {renderMakeBet()}
              {/* Soccer matches: in-play stats as their own block under the bet pad */}
              {isSportsEvent(question) && isSportsMatchLive(question) && (
                <Suspense fallback={null}>
                  <SportEventLiveStats question={question} variant="sidebar" />
                </Suspense>
              )}
            </aside>
          )}

          {/* Mobile: PlaceBet (Sheet component handles its own positioning) */}
          {isMobileSize && <div>{renderMakeBet()}</div>}
        </div>
        <HelmetMarketImage market={question} />
      </Suspense>
    )
  }

  return render()
}

export default ViewMarketPage
